import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface CityExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
}

export interface CityFormInput {
  country: string;
  state: string;
  name: string;
  code: string;
  description?: string;
  sortOrder: number;
}

export const CITY_FIELD_LABELS: Record<string, string> = {
  country: 'Country',
  state: 'State / Province',
  name: 'City name',
  code: 'Code',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const CITY_MAX_SORT_ORDER = 999999;
const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}0-9 .,'()&\-–/]*$/u;

export const normalizeCityName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Same slug the server uses for city codes. */
export function citySlug(text: string, max = 14): string {
  const s = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toUpperCase();
  return s.slice(0, max);
}

/** Code the server will assign, e.g. IN_KA_BENGALURU (suffixed _2, _3… when taken). */
export function previewCityCode(country: string, state: string, name: string, taken: Set<string>) {
  const slug = citySlug(normalizeCityName(name));
  if (!country || !state || !slug) return '';
  const base = `${country}_${state}_${slug}`;
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    if (!taken.has(`${base}_${i}`)) return `${base}_${i}`;
  }
  return base;
}

export function nextCitySortOrder(records: CityExistingRecord[], excludeId?: string) {
  return records
    .filter((r) => r._id !== excludeId)
    .reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0) + 1;
}

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

/** Field errors keyed by form field; `existing` holds the other cities of the same state. */
export function validateCityForm(
  form: CityFormInput,
  { existing, editingId }: { existing: CityExistingRecord[]; editingId?: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = normalizeCityName(form.name);
  const description = String(form.description ?? '').trim();
  const others = existing.filter((r) => r._id !== editingId);

  if (!form.country) errors.country = 'Select a country';
  if (!form.state) errors.state = form.country ? 'Select the state / province first' : 'Select a country first';

  if (!name) errors.name = 'City name is required';
  else if (name.length < 2 || name.length > 80) errors.name = 'City name must be 2–80 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Start with a letter; use letters, numbers, spaces or . , \' ( ) & - /';
  else {
    const repeat = repeatedTextError('City name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeCityName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" already exists in this state (code ${clash.code})`;
    }
  }

  if (form.state && name && !errors.name) {
    const code = form.code.trim().toUpperCase();
    if (!code.startsWith(`${form.country}_${form.state}_`)) errors.code = 'Code could not be generated — adjust the name';
    else if (others.some((r) => String(r.code || '').toUpperCase() === code)) errors.code = `Code ${code} is already used`;
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > CITY_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${CITY_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => Number(r.sortOrder) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by ${clash.name} — next available is ${nextCitySortOrder(existing, editingId)}`;
    }
  }

  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}
